import { useEffect, useMemo, useRef, useState } from "react";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import NumberDraft from "./NumberDraft";
import {
  Fact,
  More,
  RawAltToggle,
  RawModeSelect,
  Segment,
  requestText,
  requestTrail,
  sentence,
  useOpenWhen,
} from "./radarUi";
import { rtctApi } from "services/rtctApi";
import {
  commandInFlight,
  describeRequest,
  finite,
  newestWith,
  ordinal,
  pendingLiveCommand,
  radarCommandError,
  sgtTime as sgt,
  shownStatus,
  stillAsked,
  streamMaskText,
  switchOff,
  timeOf,
  trackRequest,
} from "utils/radarDiagnostics";
import {
  BAUD_FACTORY,
  FAST_STREAM_BAUD,
  RADAR_BAUDS,
  RAW_MODE_DEFAULT,
  STREAM_ADC,
  STREAM_CADENCES,
  STREAM_DUMP,
  STREAM_ECHOES,
  STREAM_SPECTRUM,
  baudFound,
  baudFoundText,
  baudSwitch,
  baudSwitchText,
  baudText,
  echoListSupported,
  echoReplyAnswer,
  packetSeconds,
  rawModeChoices,
  rawModeHint,
  rawModeText,
  readBaud,
  readMask,
  readRawAlt,
  readRawMode,
  streamFloor,
} from "utils/radarStream";
import {
  BURST_DEFAULTS,
  BURST_KEYS,
  BURST_SUN_KEY,
  burstPower,
  burstText,
  readBurst,
  readWhole,
  ruleOf,
  ruleSet,
} from "utils/radarBurst";
import {
  RICH_ADC,
  RICH_FLOOR_DEFAULT_MV,
  RICH_KEYS,
  RICH_SOLAR_SHARE,
  RICH_SPECTRUM,
  SOLAR_DAYS,
  arrivedText,
  countersNote,
  richRecommended,
  countersText,
  extraParts,
  readRich,
  readingCounts,
  readingCountsText,
  richArrived,
  richCostText,
  richCounters,
  richDayCost,
  richOn,
  richPartsText,
  richPower,
  richRule,
  richSettingsText,
  rtctBuild,
  solarInput,
  staleRichCounts,
  wakeEveryText,
} from "utils/radarRich";
import { megabytes, milliampHours, richDataLines } from "utils/radarAnalysis";
import {
  EDGE_BENCH,
  EDGE_CHOICES,
  EDGE_DEFAULTS,
  EDGE_KEYS,
  EDGE_SHORT,
  GATE_KEYS,
  KEEP_EDGES,
  KEEP_KEYS,
  edgeFirmwareNote,
  edgeName,
  edgeRule,
  edgeSettingsText,
  gateBasisText,
  gateMatchText,
  gateText,
  keepFigures,
  keepNoteText,
  keepShareText,
  readEdge,
} from "utils/radarEdge";

// Radar rich data (Node Control, in the schedules area): the settings behind
// the Radar analysis page, in one compact card. Live streaming (radar_stream,
// radar_stream_s, only while live mode runs), rich-wake bursts (radar_burst,
// radar_burst_n, radar_burst_gap_s, radar_burst_every and, from firmware .57,
// radar_burst_min_si), from firmware .58 rich readings on every wake
// (radar_rich, radar_rich_up, radar_rich_min_bv: shown where the server
// accepts them) and, from firmware .61-.62, the node's own estimate of each
// beat signal (radar_edge), the gate it searches (radar_gate_min_mm,
// radar_gate_max_mm) and .62's hybrid upload (radar_keep_every, radar_keep_mm,
// radar_keep_db) each go as one ordinary settings command; the raw output
// mode and the alternation are one setting for the node, shared by streaming
// and bursts, sent with either command or alone. The node keeps only the
// newest command, so one goes at a time. What they cost is under Before you
// apply (`onPlan` hands the page the settings to estimate); the data itself
// is on the Radar analysis page.
const PARTS = [
  [STREAM_SPECTRUM, "Spectrum"],
  [STREAM_ADC, "Beat signal"],
  [STREAM_ECHOES, "Echo list"],
  [STREAM_DUMP, "Settings dump (once)"],
];
const STREAM_DEFAULT = { mask: STREAM_SPECTRUM | STREAM_ECHOES, seconds: 30 };
const NOT_LIVE = {
  off: "Streaming runs only while the node is in live mode: start Live mode above, and these controls turn on once the node samples live.",
  starting:
    "Live mode is starting; these controls turn on once the node samples live.",
  stopping: "Live mode is stopping, and the stream stops with it.",
  armed:
    "Live mode is armed but not sampling (it waits for external power); streaming runs only while live mode samples.",
  silent:
    "Live mode is on, but no live samples have arrived lately; these controls turn on again once they do.",
};
const BURST_NUMBERS = [
  ["radar_burst_n", "n", "Captures per burst"],
  ["radar_burst_gap_s", "gap", "Pause between captures (s)"],
  ["radar_burst_every", "every", "A burst on every Nth upload"],
  [BURST_SUN_KEY, "minSi", "Only when solar ≥ (mA)"],
];
const RAW_KEYS = ["radar_raw_mode", "radar_raw_alt"];
// What a rich reading takes, the beat signal first (the recommended part).
const RICH_PARTS = [
  [RICH_ADC, "Beat signal"],
  [RICH_SPECTRUM, "Spectrum"],
];
const STREAM_KEYS = ["radar_stream", "radar_stream_s"];
const ALL_BURST_KEYS = [...BURST_KEYS, BURST_SUN_KEY];
// The hybrid upload's three rules (firmware .62), as the card edits them.
const KEEP_NUMBERS = [
  [
    "radar_keep_every",
    "every",
    "Raw captures on every Nth rich wake",
    "1 sends every rich wake's raw captures, as before; 6 sends one wake's in six and holds the others back.",
  ],
  [
    "radar_keep_mm",
    "mm",
    "Also when the estimate moves over (mm)",
    "A wake whose estimate moved more than this from the last one sent goes up too; 0 off.",
  ],
  [
    "radar_keep_db",
    "db",
    "Also when the runner-up echo is within (dB)",
    "A wake whose second-strongest echo stands this close to the strongest, allowing for range, goes up too; 0 off.",
  ],
];
const wholeNumber = (v, digits = 0) =>
  v.toLocaleString(undefined, { maximumFractionDigits: digits });

const each = (v) =>
  Number(v) === 1 ? "every upload" : `every ${ordinal(Number(v))} upload`;
// A record that rides once in a reading (firmware .60's jjy_baud, the node's
// search for the radar's rate, and jjy_baud_set, a switch's outcome): radar/'s
// latest names it for the newest reading (baud, baud_set; null when that
// reading has none), else the newest reading that carries it. -> {value, t}
// or null.
function riding(latest, samples, latestKey, readingKey) {
  const value = latest?.[latestKey];
  if (value && typeof value === "object") return { value, t: latest.t ?? null };
  return newestWith(samples, readingKey);
}
const onOff = (v) => (Number(v) ? "on" : "off");
const only = (values, allowed) =>
  allowed
    ? Object.fromEntries(
        Object.entries(values).filter(([k]) => allowed.includes(k)),
      )
    : values;

// What each setting asks and what the node reports, folded: the section's
// state line says it in words.
function Table({ label, rows, asked, reported }) {
  return (
    <More label="Requested and reported">
      <div className="field-stream-table field-rich-table">
        <table className="field-table" aria-label={label}>
          <thead>
            <tr>
              <th scope="col">Setting</th>
              <th scope="col">Requested</th>
              <th scope="col">The node reports</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(([key, name, format]) => (
              <tr key={key}>
                <th scope="row">{name}</th>
                <td>
                  {asked[key] === undefined || asked[key] === null
                    ? "—"
                    : format(asked[key])}
                </td>
                <td>
                  {reported[key] === undefined || reported[key] === null
                    ? "Not reported"
                    : format(reported[key])}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </More>
  );
}

// One part of the card, folded to its heading and state unless it is in use
// (`open`: see useOpenWhen).
function Fold({ id, title, chip = null, tone = "", open = true, children }) {
  const shown = useOpenWhen(open);
  return (
    <details
      className="field-rich-section field-fold"
      aria-labelledby={id}
      open={shown}
    >
      <summary className="field-rich-head">
        <h3 id={id} className="field-radar-heading">
          {title}
        </h3>
        {chip && <span className={`field-state ${tone}`}>{chip}</span>}
      </summary>
      <div className="field-fold-body">{children}</div>
    </details>
  );
}

// What a rich reading captures, on every wake or as well on upload wakes; a
// part taken on every wake is taken on upload wakes anyway (`taken`).
function RichParts({ label, mask, taken = 0, disabled, onChange }) {
  return (
    <div className="field-rich-parts" role="group" aria-label={label}>
      <span className="field-rich-parts-label">{label}</span>
      {RICH_PARTS.map(([bit, name]) => {
        const always = !!(taken & bit);
        return (
          <label
            key={bit}
            className={`field-rich-part${always ? " is-unavailable" : ""}`}
          >
            <input
              type="checkbox"
              checked={always || !!(mask & bit)}
              disabled={disabled || always}
              onChange={(e) =>
                onChange(e.target.checked ? mask | bit : mask & ~bit)
              }
            />
            {name}
          </label>
        );
      })}
      {(taken & 3) === 3 && (
        <small className="field-radar-hint">
          Both are taken on every wake already.
        </small>
      )}
    </div>
  );
}

function Parts({ label, mask, disabled, unsupported, answer, onChange }) {
  return (
    <div className="field-rich-parts" role="group" aria-label={label}>
      {PARTS.map(([bit, name]) => {
        const none = bit === STREAM_ECHOES && unsupported;
        return (
          <label
            key={bit}
            className={`field-rich-part${none ? " is-unavailable" : ""}`}
          >
            <input
              type="checkbox"
              checked={!!(mask & bit)}
              disabled={disabled || none}
              onChange={(e) =>
                onChange(e.target.checked ? mask | bit : mask & ~bit)
              }
            />
            {name}
          </label>
        );
      })}
      {unsupported && (
        <small className="field-radar-hint">
          This radar's firmware does not provide an echo list
          {answer
            ? `: it answers the query (AT+PEAKPREDICT) with “${answer}”`
            : ""}
          .
        </small>
      )}
    </div>
  );
}

// What rich readings cost with the raw uploads as the node runs them and,
// while others are chosen here, with those: the projection's rich line
// (keepFigures(): battery a day, capture messages sent a day of the captures
// taken, data a month, the share of rich wakes whose raw captures go up).
// `waiting`: a projection is being worked out; `answered`: the projection
// has radar lines (so none for rich readings means they are off).
const EDGE_COST_ROWS = [
  ["battery", "Battery a day"],
  ["uploads", "Capture messages sent a day"],
  ["data", "Data a month"],
  ["share", "Rich wakes whose raw captures go up"],
];
const edgeCell = {
  battery: (f) =>
    f.mahDay !== null
      ? `≈ ${milliampHours({ low: f.mahDay, high: f.mahDay })}`
      : "—",
  uploads: (f) =>
    f.uploadsDay !== null
      ? `${wholeNumber(f.uploadsDay, 1)}${f.capturesDay !== null ? ` of ${wholeNumber(f.capturesDay, 1)} captures` : ""}`
      : "—",
  data: (f) =>
    f.mbMonth !== null ? megabytes({ low: f.mbMonth, high: f.mbMonth }) : "—",
  share: (f) => (f.keep ? keepShareText(f.keep) : "Every one: none held back"),
};
function EdgeCosts({ now, planned, waiting, failed, answered, onNet = "" }) {
  if (failed)
    return (
      <p className="field-rich-note">
        The projection could not be worked out, so what the raw uploads save is
        not known.
      </p>
    );
  const line = planned || now;
  if (!line)
    return (
      <p className="field-rich-note">
        {waiting
          ? "Working out what the raw uploads save…"
          : answered
            ? "Rich readings are off at these settings: there are no rich wakes to hold back."
            : "This server's projection does not estimate rich readings yet."}
      </p>
    );
  if (line.uploadsDay === null)
    return (
      <p className="field-rich-note">
        This server's projection does not count the raw uploads yet; it needs
        the backend's .62 support.
      </p>
    );
  const columns = planned
    ? [
        ["now", "As the node runs", now],
        ["planned", "As set here", planned],
      ]
    : [["now", "At these settings", now]];
  return (
    <>
      <div className="field-stream-table field-rich-table">
        <table
          className="field-table"
          aria-label="Rich readings with these raw uploads"
        >
          <thead>
            <tr>
              <th scope="col">Rich readings</th>
              {columns.map(([key, label]) => (
                <th scope="col" key={key}>
                  {label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {EDGE_COST_ROWS.map(([row, label]) => (
              <tr key={row}>
                <th scope="row">{label}</th>
                {columns.map(([key, , figures]) => (
                  <td key={key}>
                    {figures
                      ? edgeCell[row](figures)
                      : waiting
                        ? "Calculating…"
                        : "—"}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <small className="field-radar-hint">
        From the projection, as under Before you apply{onNet}
        {planned
          ? ": the same rich readings in both columns, only the on-node estimate and the raw uploads differ."
          : "."}{" "}
        The node takes the same captures either way; it sends fewer.
      </small>
    </>
  );
}

// One radar command as this browser sent it, kept up to date from the
// settings state like the radar diagnostics' commands; `desired` is what the
// request asked for (the server's copy while it describes that request).
function tracked(local, settings, key) {
  const t = trackRequest(local, settings, key);
  return (
    t && {
      ...t,
      desired:
        t.request_id && t.request_id === settings?.request_id
          ? settings.desired
          : local?.desired || { [t.key || key]: t.value },
    }
  );
}

export default function RadarRichDataCard({
  mac,
  live = null,
  settings = null,
  samples = [],
  onSent,
  onPlan,
  // Before you apply's projection (null while it is worked out) and whether
  // it failed: the rich readings' battery a day, shown in their block. It is
  // worked out for `transport` ("wifi" | "cellular", Before you apply's
  // choice), said beside the figure when the node runs on another
  // (`nodeTransport`, from its newest reading; null when unknown).
  projection = null,
  projectionFailed = false,
  transport = null,
  nodeTransport = null,
  // The node's rich readings the server holds from the last 24 h, from
  // radar/analysis/?days=1&kind=rich ({points, total, pending}; null while
  // loading or without one): beside the counters, which trail them.
  richDay = null,
  // radar/'s latest (the newest reading as the radar view reads it), for the
  // radar link's search and switch (baud, baud_set) and the newest rich
  // wake's keep note (firmware .62); null without it.
  radarLatest = null,
  // The projection at the node's own estimate and raw-upload settings, while
  // others are chosen here (the page asks it beside Before you apply's; null
  // otherwise or while it is worked out): the Raw uploads figures compare the
  // two.
  projectionNow = null,
  projectionNowFailed = false,
  // radar/profile/'s suggested_gate (readSuggestedGate()): the gate that
  // gives the node's estimate the server's echoes; null when the server
  // suggests none, undefined while it is asked.
  suggestedGate = undefined,
}) {
  const reported = useMemo(
    () => settings?.reported || {},
    [settings?.reported],
  );
  const rules = settings?.rules || {};
  const allowed = Array.isArray(settings?.allowed_keys)
    ? settings.allowed_keys
    : null;
  const desiredNow = settings?.desired || {};

  // ---- What the node reports ----
  const reportedMask = readMask(reported.radar_stream);
  const reportedSeconds = finite(reported.radar_stream_s);
  const rb = readBurst(reported, rules);
  const rr = readRich(reported, rules);
  const reportedMode = readRawMode(reported.radar_raw_mode);
  const reportedAlt = readRawAlt(reported.radar_raw_alt);
  const running = live?.phase === "running";
  const streamReady =
    !allowed ||
    (allowed.includes("radar_stream") && allowed.includes("radar_stream_s"));
  const burstReady = !allowed || BURST_KEYS.every((k) => allowed.includes(k));
  const sunAllowed = !!allowed?.includes(BURST_SUN_KEY);
  const modeAllowed = !!allowed?.includes("radar_raw_mode");
  const altAllowed = !!allowed?.includes("radar_raw_alt");
  // Rich readings (firmware .58): only where the server accepts them.
  const richAllowed = !!allowed?.includes("radar_rich");
  const upAllowed = richAllowed && !!allowed?.includes("radar_rich_up");
  const floorAllowed = richAllowed && !!allowed?.includes("radar_rich_min_bv");
  // Firmware .59: radar_rich on every Nth wake only.
  const everyAllowed = richAllowed && !!allowed?.includes("radar_rich_every");
  const streamRule = Array.isArray(rules.radar_stream_s)
    ? rules.radar_stream_s
    : [10, 600];
  // The radar link (firmware .60): its rate as the node reads it back, else
  // as it last found the radar answering (jjy_baud's bd); every 5 s streaming
  // needs 38,400 baud or more, so below that the stream's floor is 10 s. Its
  // rule is a set of rates (set_keys), not a range.
  const baudAllowed = !!allowed?.includes("radar_baud");
  const baudSetRule = ruleSet(rules, "radar_baud", settings?.set_keys);
  const baudRange =
    !baudSetRule && Array.isArray(rules.radar_baud) ? rules.radar_baud : null;
  const reportedBaud = readBaud(reported.radar_baud);
  const detection = useMemo(
    () => riding(radarLatest, samples, "baud", "jjy_baud"),
    [radarLatest, samples],
  );
  const found = baudFound(detection?.value);
  const linkBaud = reportedBaud ?? found?.bd ?? null;
  const streamMin = streamFloor(linkBaud, streamRule);
  const modeRule = Array.isArray(rules.radar_raw_mode)
    ? rules.radar_raw_mode
    : [1, 7];
  // The node's own estimate (firmware .61-.62): where the server accepts it
  // or the node reports it. A server before the backend's .62 support allows
  // radar_edge 0-2 and no raw-upload keys.
  const re = readEdge(reported, rules);
  // Rich readings' recommended parts follow the node's estimate: the beat
  // signal alone once estimator v2 runs on it.
  const recommendedRich = richRecommended(re.edge);
  const v2Recommended = recommendedRich.mask === RICH_ADC;
  const edgeAllowed = !!allowed?.includes("radar_edge");
  const gateAllowed =
    edgeAllowed && GATE_KEYS.every((k) => allowed.includes(k));
  const keepAllowed =
    edgeAllowed && KEEP_KEYS.every((k) => allowed.includes(k));
  const edgeShown = edgeAllowed || re.edge !== null;
  const edgeRange = edgeRule(rules, "radar_edge");

  // ---- Drafts: local until a command is sent ----
  // The drafts start from what the node reports and follow its reports (the
  // settings may arrive after the card) until they are edited.
  const streamFrom = () => ({
    mask:
      reportedMask > 0 && reportedMask & ~STREAM_DUMP
        ? reportedMask & ~STREAM_DUMP
        : STREAM_DEFAULT.mask,
    seconds:
      reportedSeconds &&
      reportedSeconds >= streamMin &&
      reportedSeconds <= streamRule[1]
        ? reportedSeconds
        : STREAM_DEFAULT.seconds,
  });
  const burstFrom = () => ({
    mask:
      rb.mask > 0 && rb.mask & ~STREAM_DUMP
        ? rb.mask & ~STREAM_DUMP
        : BURST_DEFAULTS.mask,
    n: rb.n ?? BURST_DEFAULTS.n,
    gap: rb.gap ?? BURST_DEFAULTS.gap,
    every: rb.every ?? BURST_DEFAULTS.every,
    minSi: rb.minSi ?? BURST_DEFAULTS.minSi,
  });
  // A node with rich readings off starts from the recommended setup.
  const richRuns = richOn(rr);
  const richFrom = () => ({
    mask: richRuns ? (rr.mask ?? 0) : recommendedRich.mask,
    up: richRuns ? (rr.up ?? 0) : recommendedRich.up,
    minBv: rr.minBv ?? RICH_FLOOR_DEFAULT_MV,
    every: richRuns ? (rr.every ?? 1) : 1,
    burstsOff: false,
  });
  // What the node runs: its read-back, else its defaults.
  const nodeEdge = {
    edge: re.edge ?? EDGE_DEFAULTS.edge,
    gateMin: re.gateMin ?? EDGE_DEFAULTS.gateMin,
    gateMax: re.gateMax ?? EDGE_DEFAULTS.gateMax,
    every: re.every ?? EDGE_DEFAULTS.every,
    mm: re.mm ?? EDGE_DEFAULTS.mm,
    db: re.db ?? EDGE_DEFAULTS.db,
  };
  const edgeFrom = () => ({ ...nodeEdge });
  const [stream, setStream] = useState(streamFrom);
  const [burst, setBurst] = useState(burstFrom);
  const [rich, setRich] = useState(richFrom);
  const [edge, setEdge] = useState(edgeFrom);
  const touchedRef = useRef({
    stream: false,
    burst: false,
    rich: false,
    edge: false,
  });
  useEffect(() => {
    if (!touchedRef.current.stream) setStream(streamFrom());
    // streamFrom reads only these.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportedMask, reportedSeconds]);
  useEffect(() => {
    if (!touchedRef.current.burst) setBurst(burstFrom());
    // burstFrom reads only these.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rb.mask, rb.n, rb.gap, rb.every, rb.minSi]);
  useEffect(() => {
    if (!touchedRef.current.rich) setRich(richFrom());
    // richFrom reads only these.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rr.mask, rr.up, rr.minBv, rr.every]);
  useEffect(() => {
    if (!touchedRef.current.edge) setEdge(edgeFrom());
    // edgeFrom reads only these.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [re.edge, re.gateMin, re.gateMax, re.every, re.mm, re.db]);
  // null: follow what the node reports.
  const [raw, setRaw] = useState({ mode: null, alt: null });
  // The radar link's rate as picked here (null: the node's).
  const [baud, setBaud] = useState(null);
  const [touched, setTouched] = useState({
    stream: false,
    burst: false,
    rich: false,
    edge: false,
  });
  const [locals, setLocals] = useState({
    stream: null,
    burst: null,
    raw: null,
    rich: null,
    baud: null,
    edge: null,
  });
  const [busy, setBusy] = useState(null);
  const [errors, setErrors] = useState({});
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  const editStream = (patch) => {
    setStream((old) => ({ ...old, ...patch }));
    touchedRef.current.stream = true;
    setTouched((old) => ({ ...old, stream: true }));
  };
  const editBurst = (patch) => {
    setBurst((old) => ({ ...old, ...patch }));
    touchedRef.current.burst = true;
    setTouched((old) => ({ ...old, burst: true }));
  };
  const editRich = (patch) => {
    setRich((old) => ({ ...old, ...patch }));
    touchedRef.current.rich = true;
    setTouched((old) => ({ ...old, rich: true }));
  };
  const editEdge = (patch) => {
    setEdge((old) => ({ ...old, ...patch }));
    touchedRef.current.edge = true;
    setTouched((old) => ({ ...old, edge: true }));
  };

  // ---- The requests and how far they got ----
  const streamReq = tracked(locals.stream, settings, "radar_stream");
  const burstKey =
    locals.burst?.key ||
    ALL_BURST_KEYS.find((k) => k in desiredNow) ||
    BURST_KEYS[0];
  const burstReq = tracked(locals.burst, settings, burstKey);
  const richKey =
    locals.rich?.key || RICH_KEYS.find((k) => k in desiredNow) || RICH_KEYS[0];
  const richReq = tracked(locals.rich, settings, richKey);
  // A raw-output request of its own: one without stream, burst or rich keys.
  const rawOnly =
    !STREAM_KEYS.some((k) => k in desiredNow) &&
    !ALL_BURST_KEYS.some((k) => k in desiredNow) &&
    !RICH_KEYS.some((k) => k in desiredNow);
  const rawKey =
    locals.raw?.key || RAW_KEYS.find((k) => k in desiredNow) || RAW_KEYS[0];
  const rawReq = tracked(
    locals.raw,
    rawOnly ? settings : { ...settings, desired: {} },
    rawKey,
  );
  const baudReq = tracked(locals.baud, settings, "radar_baud");
  const edgeKey =
    locals.edge?.key || EDGE_KEYS.find((k) => k in desiredNow) || EDGE_KEYS[0];
  const edgeReq = tracked(locals.edge, settings, edgeKey);
  // The node keeps only the newest command, a live one included.
  const inFlight =
    commandInFlight(
      settings,
      streamReq,
      burstReq,
      rawReq,
      richReq,
      baudReq,
      edgeReq,
    ) || pendingLiveCommand(live);
  // What is still asked, newest last: the requested column of each table.
  const asked = useMemo(
    () =>
      [streamReq, burstReq, rawReq, richReq, baudReq, edgeReq]
        .filter((r) => r && stillAsked(r, settings))
        .sort((a, b) => (timeOf(a.since) ?? 0) - (timeOf(b.since) ?? 0))
        .reduce((all, r) => ({ ...all, ...(r.desired || {}) }), {}),
    [streamReq, burstReq, rawReq, richReq, baudReq, edgeReq, settings],
  );
  const send = async (section, values) => {
    setBusy(section);
    setErrors((old) => ({ ...old, [section]: "" }));
    try {
      const data = await rtctApi.applySettings(mac, only(values, allowed));
      if (!alive.current) return;
      const key =
        section === "stream"
          ? "radar_stream"
          : section === "burst"
            ? ALL_BURST_KEYS.find((k) => k in values) || BURST_KEYS[0]
            : section === "rich"
              ? RICH_KEYS.find((k) => k in values) || RICH_KEYS[0]
              : section === "baud"
                ? "radar_baud"
                : section === "edge"
                  ? EDGE_KEYS.find((k) => k in values) || EDGE_KEYS[0]
                  : RAW_KEYS.find((k) => k in values) || RAW_KEYS[0];
      setLocals((old) => ({
        ...old,
        [section]: {
          key,
          value: values[key],
          desired: only(values, allowed),
          request_id: data?.request_id ?? null,
          status: data?.status || "sent",
          since: data?.updated_at || new Date().toISOString(),
        },
      }));
      onSent?.();
    } catch (e) {
      if (alive.current)
        setErrors((old) => ({ ...old, [section]: radarCommandError(e) }));
    } finally {
      if (alive.current) setBusy(null);
    }
  };

  // ---- Whether the radar has an echo list (the node's own query) ----
  const pk = useMemo(() => newestWith(samples, "jjy_pk"), [samples]);
  const echoUnsupported = echoListSupported(pk?.value) === false;
  const echoAnswer = echoUnsupported ? echoReplyAnswer(pk.value) : "";

  // ---- The raw output: one setting, shared ----
  const modeShown = raw.mode ?? reportedMode ?? RAW_MODE_DEFAULT;
  const altShown = raw.alt ?? reportedAlt ?? 0;
  const modeChange =
    modeAllowed &&
    raw.mode !== null &&
    raw.mode !== (reportedMode ?? RAW_MODE_DEFAULT)
      ? raw.mode
      : null;
  const altChange =
    altAllowed && raw.alt !== null && raw.alt !== (reportedAlt ?? 0)
      ? raw.alt
      : null;
  const rawChanges = {
    ...(modeChange !== null ? { radar_raw_mode: modeChange } : {}),
    ...(altChange !== null ? { radar_raw_alt: altChange } : {}),
  };
  const rawChanged = Object.keys(rawChanges).length > 0;
  const rawShown =
    modeAllowed || altAllowed || reportedMode !== null || reportedAlt !== null;
  const requestedMode =
    "radar_raw_mode" in asked ? readRawMode(asked.radar_raw_mode) : null;
  const requestedAlt =
    "radar_raw_alt" in asked ? readRawAlt(asked.radar_raw_alt) : null;

  // ---- The radar link (firmware .60) ----
  // A switch's outcome rides once in the next reading (jjy_baud_set).
  const switchRecord = useMemo(
    () => riding(radarLatest, samples, "baud_set", "jjy_baud_set"),
    [radarLatest, samples],
  );
  const lastSwitch = baudSwitch(switchRecord?.value);
  const linkShown =
    baudAllowed || linkBaud !== null || lastSwitch !== null || found !== null;
  const baudChoices = Array.from(
    new Set([
      ...(baudSetRule
        ? baudSetRule.map(readBaud).filter((b) => b !== null)
        : RADAR_BAUDS.filter(
            (b) => !baudRange || (b >= baudRange[0] && b <= baudRange[1]),
          )),
      ...(reportedBaud !== null ? [reportedBaud] : []),
    ]),
  ).sort((a, b) => a - b);
  const baudShown = baud ?? reportedBaud ?? BAUD_FACTORY;
  const baudChange = baud !== null && baud !== reportedBaud ? baud : null;
  const requestedBaud =
    "radar_baud" in asked ? readBaud(asked.radar_baud) : null;
  const baudStatus = baudReq ? shownStatus(baudReq, settings) : null;
  // The node switches on its next wake: the server keeps the request
  // acknowledged until a read-back shows the rate asked for.
  const [linkChip, linkTone] = inFlight
    ? ["Waiting for the node", "is-waiting"]
    : baudStatus === "acknowledged"
      ? ["Switches on its next wake", "is-waiting"]
      : linkBaud !== null
        ? [`${linkBaud.toLocaleString()} baud`, "is-running"]
        : ["Not reported yet", "is-off"];
  // One of the radar's packets on the wire: "1.1 s", "0.27 s".
  const onWire = (b) => {
    const t = packetSeconds(b);
    return `${t < 0.95 ? t.toFixed(2) : t.toFixed(1)} s`;
  };

  // ---- Live streaming ----
  const streamMask = echoUnsupported
    ? stream.mask & ~STREAM_ECHOES
    : stream.mask;
  const requestedStream =
    "radar_stream" in asked ? readMask(asked.radar_stream) : null;
  const nodeStreams = (reportedMask ?? 0) > 0 || (requestedStream ?? 0) > 0;
  const streamSame =
    reportedMask > 0 &&
    !(streamMask & STREAM_DUMP) &&
    streamMask === (reportedMask & ~STREAM_DUMP) &&
    stream.seconds === reportedSeconds;
  const streamEdit = running && streamReady && !busy;
  const streamSend = streamEdit && !inFlight;
  const streamStatus = streamReq ? shownStatus(streamReq, settings) : null;
  const streamOff = useMemo(
    () =>
      switchOff(
        samples,
        "jjy_stream_err",
        timeOf(streamReq?.since) ?? -Infinity,
      ),
    [samples, streamReq?.since],
  );
  const cadences = Array.from(
    new Set(
      [...STREAM_CADENCES, stream.seconds, reportedSeconds].filter(Boolean),
    ),
  )
    .filter((s) => s >= streamMin && s <= streamRule[1])
    .sort((a, b) => a - b)
    .map((s) => [s, `${s} s`]);
  const streamHint =
    modeAllowed || altAllowed
      ? rawModeHint(modeShown, streamMask, {
          alt: altShown === 1,
          altAllowed,
        })
      : streamMask & STREAM_ADC && modeShown === 1
        ? "The node runs the radar's raw output in mode 1, which sends only the spectrum, so no beat signal arrives."
        : "";
  const [streamChip, streamTone] = !live
    ? ["Checking live mode", "is-off"]
    : !running
      ? ["Needs live mode", "is-off"]
      : inFlight
        ? ["Waiting for the node", "is-waiting"]
        : (reportedMask ?? 0) > 0
          ? [`On · every ${reportedSeconds ?? "?"} s`, "is-running"]
          : ["Off", "is-off"];

  // ---- Classic bursts ----
  const burstMask = echoUnsupported ? burst.mask & ~STREAM_ECHOES : burst.mask;
  const requestedBurst =
    "radar_burst" in asked ? readMask(asked.radar_burst) : null;
  const nodeBursts = (rb.mask ?? 0) > 0 || (requestedBurst ?? 0) > 0;
  const burstSame =
    rb.mask > 0 &&
    !(burstMask & STREAM_DUMP) &&
    burstMask === (rb.mask & ~STREAM_DUMP) &&
    burst.n === rb.n &&
    burst.gap === rb.gap &&
    burst.every === rb.every &&
    (!sunAllowed || burst.minSi === (rb.minSi ?? 0));
  const burstEdit = burstReady && !busy;
  const burstSend = burstEdit && !inFlight;
  const burstStatus = burstReq ? shownStatus(burstReq, settings) : null;
  const burstOff = useMemo(
    () =>
      switchOff(samples, "jjy_burst_err", timeOf(burstReq?.since) ?? -Infinity),
    [samples, burstReq?.since],
  );
  const newestSample = useMemo(
    () =>
      samples.reduce(
        (best, x) =>
          (timeOf(x?.timestamp) ?? -Infinity) >
          (timeOf(best?.timestamp) ?? -Infinity)
            ? x
            : best,
        null,
      ),
    [samples],
  );
  // Firmware .58 takes the floor from radar_rich_min_bv, for a readable
  // cell whatever the input reads.
  const power = burstPower(
    newestSample,
    rr.minBv !== null
      ? {
          extV: live?.ext_power_v ?? undefined,
          floorV: rr.minBv / 1000,
          cell: true,
        }
      : {
          extV: live?.ext_power_v ?? undefined,
          floorV: live?.battery_floor_v ?? undefined,
        },
  );
  const burstValues = {
    radar_burst: burstMask,
    radar_burst_n: burst.n,
    radar_burst_gap_s: burst.gap,
    radar_burst_every: burst.every,
    ...(sunAllowed ? { [BURST_SUN_KEY]: burst.minSi } : {}),
  };
  const [burstChip, burstTone] = inFlight
    ? ["Waiting for the node", "is-waiting"]
    : rb.mask === null
      ? ["Not reported yet", "is-off"]
      : rb.mask > 0
        ? [
            `On · every ${rb.every === null || rb.every === 1 ? "" : `${ordinal(rb.every)} `}upload`,
            "is-running",
          ]
        : ["Off", "is-off"];

  // ---- Rich readings (every wake) ----
  const burstsRun = (rb.mask ?? 0) > 0;
  const richExtra = extraParts(rich.mask, rich.up);
  const richValues = {
    radar_rich: rich.mask,
    ...(upAllowed ? { radar_rich_up: richExtra } : {}),
    ...(floorAllowed ? { radar_rich_min_bv: rich.minBv } : {}),
    ...(everyAllowed ? { radar_rich_every: rich.every } : {}),
    // The recommended setup turns the classic bursts off in the same
    // command: the node keeps only the newest one.
    ...(rich.burstsOff && burstsRun ? { radar_burst: 0 } : {}),
  };
  const requestedRich =
    "radar_rich" in asked ? readWhole(asked.radar_rich, [0, 3]) : null;
  const nodeRich = richRuns || (requestedRich ?? 0) > 0;
  const richNothing = (rich.mask & 3) === 0 && (!upAllowed || !richExtra);
  const richSame =
    rr.mask !== null &&
    rich.mask === rr.mask &&
    (!upAllowed || richExtra === extraParts(rr.mask, rr.up ?? 0)) &&
    (!floorAllowed || rich.minBv === rr.minBv) &&
    (!everyAllowed || rich.every === (rr.every ?? 1)) &&
    !(rich.burstsOff && burstsRun);
  const richEdit = richAllowed && !busy;
  const richSend = richEdit && !inFlight;
  const richStatus = richReq ? shownStatus(richReq, settings) : null;
  const recommended =
    rich.mask === recommendedRich.mask &&
    (!upAllowed || richExtra === recommendedRich.up) &&
    (!everyAllowed || rich.every === 1) &&
    (!burstsRun || rich.burstsOff);
  // The node's counters: the settings endpoint's `counters`, else its
  // read-back, else a reading.
  const nodeCounters = settings?.counters;
  const counters = useMemo(
    () => richCounters(nodeCounters, reported, samples),
    [nodeCounters, reported, samples],
  );
  // What has arrived, beside the counters, which trail it.
  const arrived = useMemo(() => richArrived(richDay), [richDay]);
  // Firmware .58-.59 send their counters again only with a settings change
  // (staleRichCounts()): there captured and failed come from the readings of
  // the last 24 h, the queue's from the node; from .60 the node's own four.
  // Each with the time the node took them (counters_at).
  const fw = newestWith(samples, "fw")?.value;
  const build = rtctBuild(fw);
  const uncounted = staleRichCounts(fw);
  const countersAt = settings?.counters_at ?? null;
  const fromReadings = useMemo(
    () =>
      uncounted === null
        ? null
        : readingCounts(samples, Date.now() - 24 * 3600000),
    [uncounted, samples],
  );
  const shownCounters =
    uncounted !== null && counters
      ? { ...counters, ok: null, fail: null }
      : counters;
  const countersWords = countersText(shownCounters);
  const floorRule = richRule(rules, "radar_rich_min_bv");
  const everyRule = richRule(rules, "radar_rich_every");
  // A readable cell under the node's floor skips rich readings and bursts:
  // said for what runs.
  const richLow = richPower(newestSample, rr.minBv ?? RICH_FLOOR_DEFAULT_MV);
  const floorBursts = burstsRun && rr.minBv !== null;
  const richCaution =
    richAllowed && !!richLow?.low && (richRuns || floorBursts);
  const skipped =
    richRuns && floorBursts
      ? "rich readings and bursts"
      : richRuns
        ? "rich readings"
        : "bursts";
  const [richChip, richTone] = inFlight
    ? ["Waiting for the node", "is-waiting"]
    : rr.mask === null
      ? ["Not reported yet", "is-off"]
      : richRuns
        ? [`On · ${wakeEveryText(rr.every)}`, "is-running"]
        : ["Off", "is-off"];

  // ---- What rich readings cost the battery a day (the projection's rich
  // line, at the settings planned here or run), against what the panel
  // brought in lately ----
  const projected = useMemo(() => richDataLines(projection), [projection]);
  const richLine = projected?.find((l) => l.key === "rich") || null;
  const solar = useMemo(() => solarInput(samples), [samples]);

  // ---- What Before you apply estimates: the settings planned here once
  // edited and not what the node runs, else what it runs ----
  const streamPlanned = touched.stream && !streamSame;
  const richPlanned = touched.rich && !richSame;

  // ---- The on-node estimate (firmware .61-.62) ----
  // One command for the estimate, its gate and the hybrid upload: the
  // estimate always (the gate and the raw uploads mean nothing without it),
  // the gate's two ends together when either changed (the server checks the
  // pair; a gate whose start is not nearer than its end, the node puts back
  // to its defaults) and the three raw-upload rules together when one
  // changed.
  const gateChanged =
    gateAllowed &&
    (edge.gateMin !== nodeEdge.gateMin || edge.gateMax !== nodeEdge.gateMax);
  const keepChanged =
    keepAllowed &&
    (edge.every !== nodeEdge.every ||
      edge.mm !== nodeEdge.mm ||
      edge.db !== nodeEdge.db);
  const edgeValues = {
    radar_edge: edge.edge,
    ...(gateChanged
      ? { radar_gate_min_mm: edge.gateMin, radar_gate_max_mm: edge.gateMax }
      : {}),
    ...(keepChanged
      ? {
          radar_keep_every: edge.every,
          radar_keep_mm: edge.mm,
          radar_keep_db: edge.db,
        }
      : {}),
  };
  const edgeSame = edge.edge === nodeEdge.edge && !gateChanged && !keepChanged;
  const gateBad = gateAllowed && edge.gateMin >= edge.gateMax;
  const edgeEdit = edgeAllowed && !busy;
  const edgeSend = edgeEdit && !inFlight;
  const edgeStatus = edgeReq ? shownStatus(edgeReq, settings) : null;
  // The raw uploads mean something only with estimator v2 on the node.
  const keepUsable = KEEP_EDGES.includes(edge.edge);
  // What the projection reads (not the gate): the estimate and the raw
  // uploads, planned once edited and not what the node runs.
  const edgePlanned =
    touched.edge && (edge.edge !== nodeEdge.edge || keepChanged);
  const requestedEdge =
    "radar_edge" in asked ? readEdge(asked, rules).edge : null;
  const nodeEstimates = (re.edge ?? 0) > 0 || (requestedEdge ?? 0) > 0;
  // The node estimates the beat signals it captures: the rich readings'
  // (as planned here, else as it runs), the bursts' or the stream's.
  const richBeat =
    ((richPlanned ? rich.mask | richExtra : (rr.mask ?? 0) | (rr.up ?? 0)) &
      RICH_ADC) >
    0;
  const capturesBeat =
    richBeat ||
    ((rb.mask ?? 0) & STREAM_ADC) > 0 ||
    ((reportedMask ?? 0) & STREAM_ADC) > 0 ||
    reportedAlt === 1;
  const edgeFirmware = edgeFirmwareNote(edge.edge, build);
  const [edgeChip, edgeTone] = inFlight
    ? ["Waiting for the node", "is-waiting"]
    : re.edge === null
      ? ["Not reported yet", "is-off"]
      : re.edge > 0
        ? [`On · ${EDGE_SHORT[re.edge] || re.edge}`, "is-running"]
        : ["Off", "is-off"];
  const gate = suggestedGate || null;
  const gateChosen =
    !!gate && edge.gateMin === gate.min && edge.gateMax === gate.max;
  const gateRuns =
    !!gate && nodeEdge.gateMin === gate.min && nodeEdge.gateMax === gate.max;
  // A request still on its way that carries it.
  const gateAsked =
    !!gate &&
    Number(asked.radar_gate_min_mm) === gate.min &&
    Number(asked.radar_gate_max_mm) === gate.max;
  // The newest rich wake's keep note: radar/'s latest, else the readings.
  const lastKeep = useMemo(
    () => riding(radarLatest, samples, "keep", "keep"),
    [radarLatest, samples],
  );
  // What the raw uploads cost: the projection's rich line at the settings
  // chosen here and, while they differ from the node's, at the node's.
  const planFigures = keepFigures(richLine);
  const nowLine = useMemo(
    () => richDataLines(projectionNow)?.find((l) => l.key === "rich") || null,
    [projectionNow],
  );
  const nowFigures = edgePlanned ? keepFigures(nowLine) : planFigures;
  const edgeLabel =
    busy === "edge"
      ? "Sending…"
      : edge.edge === 0 && nodeEstimates
        ? "Turn the on-node estimate off"
        : nodeEstimates
          ? "Update the on-node estimate"
          : "Start the on-node estimate";
  const cost = richDayCost(richLine, {
    every: richPlanned ? rich.every : (rr.every ?? 1),
  });
  const share =
    cost && solar && solar.mahDay > 0 ? cost.mahDay / solar.mahDay : null;
  // Firmware .60: a capture modelled at a faster rate than 9,600 baud - the
  // projection's own warning about it, said beside the figure.
  const modelledBaud =
    cost && !cost.measured && cost.baud !== null && cost.baud !== 9600
      ? (Array.isArray(projection?.warnings) ? projection.warnings : []).find(
          (w) => typeof w === "string" && w.includes(`${cost.baud} baud`),
        ) || null
      : null;
  const chosen = recommended && (touched.rich || richSame);
  // The figure, or why there is none.
  const planRich = richPlanned ? richNothing === false : richRuns;
  // Before you apply works it out for its own choice of network: on another
  // than the node's, the figure is that scenario's, not the node's.
  const net = (t) => (t === "cellular" ? "cellular" : "Wi-Fi");
  const onNet = !transport
    ? ""
    : !nodeTransport
      ? `, on ${net(transport)}`
      : transport === nodeTransport
        ? `, on ${net(transport)} as the node runs`
        : `, for ${net(transport)} as chosen in Before you apply (the node runs on ${net(nodeTransport)})`;
  const [costValue, costWhy] = cost
    ? [
        `≈ ${milliampHours({ low: cost.mahDay, high: cost.mahDay })}`,
        `From the projection ${richPlanned || baudChange !== null || edgePlanned ? "for the settings chosen here" : "at the node's settings"}${onNet}: ${richCostText(cost, { every: richPlanned ? rich.every : rr.every })}.`,
      ]
    : projectionFailed
      ? ["Not estimated", "The projection could not be worked out."]
      : !projection
        ? ["Calculating…", "The projection is being worked out."]
        : projected && !planRich
          ? [
              "None",
              "Rich readings are off at these settings. Choose what to capture, or use the recommended setup, to see what it costs a day.",
            ]
          : [
              "Not estimated",
              "This server's projection does not estimate rich readings yet; Before you apply estimates their data.",
            ];
  // The figure's working (folded under it), or why there is none (said).
  const costText = `${costWhy} ${
    solar
      ? `The panel brought in about ${milliampHours({ low: solar.mahDay, high: solar.mahDay })} a day over the last ${SOLAR_DAYS} days (from the readings' solar current)${share !== null ? `: this takes ${Math.round(share * 100)} % of it` : ""}.`
      : `What the panel brings in is not known: the readings at hand cover too little of the last ${SOLAR_DAYS} days.`
  }`;
  const burstsOffPlanned = richPlanned && rich.burstsOff && burstsRun;
  const burstPlanned = (touched.burst && !burstSame) || burstsOffPlanned;
  const plan = {
    stream: streamPlanned
      ? { radar_stream: streamMask, radar_stream_s: stream.seconds }
      : reportedMask !== null
        ? { radar_stream: reportedMask, radar_stream_s: reportedSeconds }
        : null,
    burst: burstsOffPlanned
      ? {
          ...(touched.burst && !burstSame
            ? burstValues
            : {
                radar_burst: rb.mask,
                radar_burst_n: rb.n,
                radar_burst_gap_s: rb.gap,
                radar_burst_every: rb.every,
                ...(rb.minSi !== null ? { [BURST_SUN_KEY]: rb.minSi } : {}),
              }),
          radar_burst: 0,
        }
      : burstPlanned
        ? burstValues
        : rb.mask !== null
          ? {
              radar_burst: rb.mask,
              radar_burst_n: rb.n,
              radar_burst_gap_s: rb.gap,
              radar_burst_every: rb.every,
              ...(rb.minSi !== null ? { [BURST_SUN_KEY]: rb.minSi } : {}),
            }
          : null,
    raw: {
      radar_raw_mode: modeChange ?? reportedMode,
      radar_raw_alt: altChange ?? reportedAlt,
    },
    // Rich readings, where the server accepts them or the node reports them.
    rich:
      richAllowed || rr.mask !== null
        ? richPlanned
          ? {
              radar_rich: rich.mask,
              radar_rich_up: richExtra,
              radar_rich_min_bv: rich.minBv,
              ...(everyAllowed ? { radar_rich_every: rich.every } : {}),
            }
          : rr.mask !== null
            ? {
                radar_rich: rr.mask,
                ...(rr.up !== null ? { radar_rich_up: rr.up } : {}),
                ...(rr.minBv !== null ? { radar_rich_min_bv: rr.minBv } : {}),
                ...(rr.every !== null ? { radar_rich_every: rr.every } : {}),
              }
            : null
        : null,
    // The radar's rate as chosen in Radar link: a modelled capture's time
    // scales with it.
    baud: baudChange !== null ? { radar_baud: baudChange } : null,
    // The node's own estimate and the raw uploads (firmware .61-.62), where
    // the server accepts them or the node reports them: their charge and
    // bytes, and the raw captures held back.
    edge: !edgeShown
      ? null
      : edgePlanned
        ? {
            radar_edge: edge.edge,
            ...(keepAllowed
              ? {
                  radar_keep_every: edge.every,
                  radar_keep_mm: edge.mm,
                  radar_keep_db: edge.db,
                }
              : {}),
          }
        : re.edge !== null
          ? {
              radar_edge: re.edge,
              ...(re.every !== null ? { radar_keep_every: re.every } : {}),
              ...(re.mm !== null ? { radar_keep_mm: re.mm } : {}),
              ...(re.db !== null ? { radar_keep_db: re.db } : {}),
            }
          : null,
    planned: {
      stream: streamPlanned,
      burst: burstPlanned,
      raw: rawChanged,
      rich: richPlanned,
      baud: baudChange !== null,
      edge: edgePlanned,
    },
  };
  const planKey = JSON.stringify(plan);
  const onPlanRef = useRef(onPlan);
  onPlanRef.current = onPlan;
  useEffect(() => {
    onPlanRef.current?.(JSON.parse(planKey));
  }, [planKey]);

  // ---- Which parts open: those in use, or holding a request on its way or
  // not applied, a caution, an error or a change made here; the others fold
  // to their heading and state (Rich readings and the on-node estimate stay
  // open) ----
  const attention = (req) =>
    !!req &&
    (stillAsked(req, settings) || shownStatus(req, settings) === "not_applied");
  const streamOpen =
    running ||
    nodeStreams ||
    touched.stream ||
    attention(streamReq) ||
    !!streamOff ||
    !!errors.stream;
  const burstOpen =
    nodeBursts ||
    touched.burst ||
    attention(burstReq) ||
    !!burstOff ||
    !!errors.burst;
  const rawOpen = rawChanged || attention(rawReq) || !!errors.raw;
  const linkOpen =
    baudChange !== null ||
    baudStatus === "acknowledged" ||
    attention(baudReq) ||
    !!errors.baud;
  const rawChip =
    reportedAlt === 1
      ? "Alternating"
      : reportedMode !== null
        ? `Mode ${reportedMode}`
        : null;

  // One choice of the on-node estimate (the bench-test ones are folded).
  const edgeChoice = ([value, name, hint]) => {
    const outside = value < edgeRange[0] || value > edgeRange[1];
    return (
      <label
        key={value}
        className={`field-edge-choice${edge.edge === value ? " is-chosen" : ""}${outside ? " is-unavailable" : ""}`}
      >
        <input
          type="radio"
          name="field-rich-edge-choice"
          value={value}
          checked={edge.edge === value}
          disabled={!edgeEdit || outside}
          onChange={() => editEdge({ edge: value })}
        />
        <span>
          <b>{name}</b>
          <small>
            {outside
              ? `${hint} This server accepts radar_edge up to ${edgeRange[1]}: it needs the backend's .62 support.`
              : hint}
          </small>
        </span>
      </label>
    );
  };

  const trail = (req, status, off, words = null) => (
    <p className="field-radar-trail" role="status">
      {requestTrail(describeRequest(req), req, status, off, words)}
      {req.followedAt
        ? ` A newer settings change followed ${sgt(req.followedAt)}; if the node had not picked this one up, it was replaced.`
        : ""}
    </p>
  );
  const rawLabel =
    busy === "raw"
      ? "Sending…"
      : modeChange === null
        ? altChange === 1
          ? "Start alternating"
          : altChange === 0
            ? "Stop alternating"
            : "Change the raw output"
        : "Change the raw output mode";

  return (
    <section
      className="field-panel field-rich"
      aria-labelledby="field-rich-title"
    >
      <div className="field-panel-head">
        <div>
          <h2 id="field-rich-title">Radar rich data</h2>
          <p>
            What the radar captures for Radar analysis, and how it is sent · one
            command at a time
          </p>
        </div>
        <Link className="field-button" to="/radar-analysis">
          Open Radar analysis <ArrowRight size={14} aria-hidden="true" />
        </Link>
      </div>
      <div className="field-panel-body field-rich-body">
        {inFlight && (
          <div className="field-radar-caution" role="note">
            {sentence(describeRequest(inFlight))} sent {sgt(inFlight.since)} is
            still on its way to the node ({requestText(inFlight.status)}). The
            node keeps only the newest command, so the next one can go once this
            one is acknowledged.
          </div>
        )}
        <Fold
          id="field-rich-stream"
          title="Live streaming"
          chip={streamChip}
          tone={streamTone}
          open={streamOpen}
        >
          {!running && (
            <p className="field-rich-note">
              {live
                ? NOT_LIVE[live.phase] || NOT_LIVE.off
                : "Checking live mode…"}
              {live && (reportedMask ?? 0) > 0
                ? ` The node keeps radar_stream=${reportedMask} and streams again in the next live session.`
                : ""}
            </p>
          )}
          {!streamReady && (
            <div className="field-notice">
              This server does not accept the stream settings (radar_stream,
              radar_stream_s) yet; it needs the updated backend.
            </div>
          )}
          <Parts
            label="What to stream"
            mask={streamMask}
            disabled={!streamEdit}
            unsupported={echoUnsupported}
            answer={echoAnswer}
            onChange={(mask) => editStream({ mask })}
          />
          <Segment
            label="Every"
            options={cadences}
            value={stream.seconds}
            onChange={(seconds) => editStream({ seconds })}
            disabled={!streamEdit}
          />
          {streamHint && (
            <small className="field-radar-hint">{streamHint}</small>
          )}
          {streamRule[0] < 10 && streamMin >= 10 && (
            <small className="field-radar-hint">
              Every 5 s needs the radar link at{" "}
              {FAST_STREAM_BAUD.toLocaleString()} baud or faster
              {linkBaud !== null
                ? `; the radar answers at ${linkBaud.toLocaleString()} baud`
                : ""}{" "}
              (Radar link, below).
            </small>
          )}
          {running && reportedMask === null && streamReady && (
            <p className="field-rich-note">
              The node has not reported radar_stream yet. Firmware that streams
              reports radar_stream and radar_stream_s with its settings; older
              firmware ignores the command.
            </p>
          )}
          {errors.stream && (
            <div className="field-alert" role="alert">
              {errors.stream}
            </div>
          )}
          <div className="field-actions">
            <button
              type="button"
              className="field-button primary"
              disabled={!streamSend || streamMask === 0 || streamSame}
              onClick={() =>
                send("stream", {
                  radar_stream: streamMask,
                  radar_stream_s: stream.seconds,
                  ...rawChanges,
                })
              }
            >
              {busy === "stream"
                ? "Sending…"
                : nodeStreams
                  ? "Update the stream"
                  : streamMask === STREAM_DUMP
                    ? "Request the settings dump"
                    : "Start streaming"}
            </button>
            <button
              type="button"
              className="field-button danger"
              disabled={!streamSend || !nodeStreams}
              onClick={() => send("stream", { radar_stream: 0 })}
            >
              Stop streaming
            </button>
          </div>
          {streamSame && (
            <small className="field-radar-hint">
              That is what the node streams now.
            </small>
          )}
          {running && streamMask === 0 && (
            <small className="field-radar-hint">
              Tick at least one part{nodeStreams ? ", or stop the stream" : ""}.
            </small>
          )}
          {echoUnsupported &&
          reportedMask > 0 &&
          reportedMask & STREAM_ECHOES ? (
            <small className="field-radar-hint">
              The node still asks the radar for its echo list; updating the
              stream leaves it out.
            </small>
          ) : null}
          {streamReq && trail(streamReq, streamStatus, streamOff)}
          {streamOff &&
            streamStatus !== "not_applied" &&
            reportedMask === 0 && (
              <div className="field-radar-caution" role="note">
                {`The node ${streamOff.text}; reported ${sgt(streamOff.t)}.`}
              </div>
            )}
          <Table
            label="Stream settings, requested and reported"
            asked={asked}
            reported={reported}
            rows={[
              [
                "radar_stream",
                "What to stream",
                (v) => (Number(v) === 0 ? "off" : streamMaskText(v)),
              ],
              ["radar_stream_s", "Every", (v) => `${v} s`],
            ]}
          />
        </Fold>

        <Fold
          id="field-rich-burst"
          title="Classic bursts"
          chip={burstChip}
          tone={burstTone}
          open={burstOpen}
        >
          <p className="field-rich-note">
            A short burst of captures on a wake that uploads, sent with that
            wake's data.
          </p>
          {!burstReady && (
            <div className="field-notice">
              This server does not accept all the burst settings (radar_burst,
              radar_burst_n, radar_burst_gap_s, radar_burst_every) yet; it needs
              the updated backend.
            </div>
          )}
          {rb.mask === null && (
            <p className="field-rich-note">
              The node has not reported radar_burst yet. Firmware rtct.56 or
              later reports the burst settings with its other settings; older
              firmware ignores them.
            </p>
          )}
          <Parts
            label="What to capture"
            mask={burstMask}
            disabled={!burstEdit}
            unsupported={echoUnsupported}
            answer={echoAnswer}
            onChange={(mask) => editBurst({ mask })}
          />
          {(modeAllowed || altAllowed) &&
            rawModeHint(modeShown, burstMask, {
              alt: altShown === 1,
              altAllowed,
            }) && (
              <small className="field-radar-hint">
                {rawModeHint(modeShown, burstMask, {
                  alt: altShown === 1,
                  altAllowed,
                })}
              </small>
            )}
          <div className="field-rich-numbers">
            {BURST_NUMBERS.filter(
              ([key]) =>
                key !== BURST_SUN_KEY || sunAllowed || rb.minSi !== null,
            ).map(([key, field, label]) => {
              const [lo, hi] = ruleOf(rules, key);
              return (
                <div className="field-burst-number" key={key}>
                  <label htmlFor={`field-rich-${field}`}>
                    {label}
                    <small>
                      {key === BURST_SUN_KEY
                        ? `0 bursts whatever the sun; otherwise only while the panel gives at least this. ${lo}–${hi}.`
                        : `${lo}–${hi}.`}
                    </small>
                  </label>
                  <NumberDraft
                    id={`field-rich-${field}`}
                    min={lo}
                    max={hi}
                    step={1}
                    value={burst[field]}
                    disabled={
                      !burstEdit || (key === BURST_SUN_KEY && !sunAllowed)
                    }
                    onChange={(v) => {
                      const value = readWhole(Math.round(v), [lo, hi]);
                      if (value !== null) editBurst({ [field]: value });
                    }}
                  />
                </div>
              );
            })}
          </div>
          {power?.skips && !richCaution && (
            <div className="field-radar-caution" role="note">
              {`The battery reads ${power.battery.toFixed(2)} V, below ${power.floorV.toFixed(2)} V: the node skips bursts until ${rr.minBv !== null ? "the cell recovers, even on external power" : "it recovers or is on external power"}.`}
            </div>
          )}
          {errors.burst && (
            <div className="field-alert" role="alert">
              {errors.burst}
            </div>
          )}
          <div className="field-actions">
            <button
              type="button"
              className="field-button primary"
              disabled={!burstSend || burstMask === 0 || burstSame}
              onClick={() => send("burst", { ...burstValues, ...rawChanges })}
            >
              {busy === "burst"
                ? "Sending…"
                : nodeBursts
                  ? "Update bursts"
                  : burstMask === STREAM_DUMP
                    ? "Request the settings dump"
                    : "Start bursts"}
            </button>
            <button
              type="button"
              className="field-button danger"
              disabled={!burstSend || !nodeBursts}
              onClick={() => send("burst", { radar_burst: 0 })}
            >
              Turn bursts off
            </button>
          </div>
          {burstSame && (
            <small className="field-radar-hint">
              That is what the node runs now.
            </small>
          )}
          {burstMask === 0 && (
            <small className="field-radar-hint">
              Tick at least one part{nodeBursts ? ", or turn bursts off" : ""}.
            </small>
          )}
          {burstReq && trail(burstReq, burstStatus, burstOff)}
          {burstOff && burstStatus !== "not_applied" && rb.mask === 0 && (
            <div className="field-radar-caution" role="note">
              {`The node ${burstOff.text}; reported ${sgt(burstOff.t)}.`}
            </div>
          )}
          <Table
            label="Burst settings, requested and reported"
            asked={asked}
            reported={reported}
            rows={[
              [
                "radar_burst",
                "What to capture",
                (v) => (Number(v) === 0 ? "off" : streamMaskText(v)),
              ],
              ["radar_burst_n", "Captures per burst", (v) => String(v)],
              ["radar_burst_gap_s", "Pause between captures", (v) => `${v} s`],
              ["radar_burst_every", "A burst on", each],
              ...(sunAllowed || rb.minSi !== null
                ? [
                    [
                      BURST_SUN_KEY,
                      "Only when solar",
                      (v) => (Number(v) > 0 ? `≥ ${v} mA` : "whatever the sun"),
                    ],
                  ]
                : []),
            ]}
          />
          {rb.mask > 0 && (
            <p className="field-rich-note">The node runs: {burstText(rb)}.</p>
          )}
        </Fold>

        {richAllowed && (
          <Fold
            id="field-rich-readings"
            title="Rich readings (every wake)"
            chip={richChip}
            tone={richTone}
          >
            <p className="field-rich-note">
              A raw capture on every wake, sent with the node's uploads, so the
              newest can be up to an upload interval old.
            </p>
            <div className="field-rich-recommended" role="note">
              <div>
                <p>
                  <b>Recommended:</b>{" "}
                  {v2Recommended
                    ? "the beat signal on every wake, nothing extra on upload wakes, and the classic hourly bursts off."
                    : "beat signal and spectrum on every wake, nothing extra on upload wakes, and the classic hourly bursts off."}
                </p>
                <More label="Why?" className="is-why">
                  <p className="field-rich-note">
                    {v2Recommended
                      ? "The node's leading-edge estimate (v2) and ours both read the beat signal, which also gives the phase (micro-movement). The spectrum is the radar's own view: a second capture on every wake, about 40 % more capture energy, that only cross-checks ours - add it if you want that check."
                      : "The beat signal gives the precise position and the phase (micro-movement); the spectrum is the radar's own view, to check ours against. Each part is a capture of its own, so a wake takes two."}
                  </p>
                </More>
              </div>
              <button
                type="button"
                className="field-button"
                disabled={!richEdit || chosen}
                onClick={() =>
                  editRich({
                    mask: recommendedRich.mask,
                    up: recommendedRich.up,
                    every: 1,
                    burstsOff: burstsRun,
                  })
                }
              >
                {chosen
                  ? "Recommended setup chosen"
                  : "Use the recommended setup"}
              </button>
            </div>
            <div
              className="field-rich-cost"
              role="group"
              aria-label="Rich readings, battery a day"
            >
              <span className="field-live-label">
                Rich readings, battery a day
              </span>
              <strong>{costValue}</strong>
              {share !== null && share <= RICH_SOLAR_SHARE && (
                <small>
                  {`${Math.round(share * 100)} % of what the solar panel brought in a day lately`}
                </small>
              )}
              {cost ? (
                <More label="How it is worked out">
                  <small>{costText}</small>
                </More>
              ) : (
                <small>{costText}</small>
              )}
            </div>
            {modelledBaud && (
              <div className="field-radar-caution" role="note">
                {modelledBaud}
              </div>
            )}
            {share !== null && share > RICH_SOLAR_SHARE && (
              <div className="field-radar-caution" role="note">
                {`Rich readings at these settings would take ${Math.round(share * 100)} % of what the panel brought in a day lately, over ${Math.round(RICH_SOLAR_SHARE * 100)} %: the battery may run down. ${everyAllowed ? "A reading on every 2nd or 3rd wake, or " : ""}${everyAllowed ? "the" : "The"} beat signal alone costs less; the recommended setup stays available.`}
              </div>
            )}
            {rr.mask === null && (
              <p className="field-rich-note">
                The node has not reported radar_rich yet. Firmware rtct.58 or
                later reports the rich-reading settings with its other settings;
                older firmware ignores them.
              </p>
            )}
            <RichParts
              label="On every wake"
              mask={rich.mask}
              disabled={!richEdit}
              onChange={(mask) => editRich({ mask })}
            />
            {upAllowed && (
              <RichParts
                label="As well on upload wakes"
                mask={rich.up}
                taken={rich.mask}
                disabled={!richEdit}
                onChange={(up) => editRich({ up })}
              />
            )}
            {(floorAllowed || everyAllowed) && (
              <div className="field-rich-numbers">
                {everyAllowed && (
                  <div className="field-burst-number">
                    <label htmlFor="field-rich-nth">
                      A reading on every Nth wake
                      <small>
                        1 every wake, 2 every other wake, up to {everyRule[1]}.
                        The upload wakes' extra parts are taken on every upload
                        wake all the same.
                      </small>
                    </label>
                    <NumberDraft
                      id="field-rich-nth"
                      min={everyRule[0]}
                      max={everyRule[1]}
                      step={1}
                      value={rich.every}
                      disabled={!richEdit}
                      onChange={(v) => {
                        const value = readWhole(Math.round(v), everyRule);
                        if (value !== null) editRich({ every: value });
                      }}
                    />
                  </div>
                )}
                {floorAllowed && (
                  <div className="field-burst-number">
                    <label htmlFor="field-rich-floor">
                      Battery floor (mV)
                      <small>
                        A readable cell must be at least this, or captures are
                        skipped (classic bursts too).{" "}
                        {floorRule[0].toLocaleString()}–
                        {floorRule[1].toLocaleString()}; the firmware's default
                        is {RICH_FLOOR_DEFAULT_MV.toLocaleString()}.
                      </small>
                    </label>
                    <NumberDraft
                      id="field-rich-floor"
                      min={floorRule[0]}
                      max={floorRule[1]}
                      step={10}
                      value={rich.minBv}
                      disabled={!richEdit}
                      onChange={(v) => {
                        const value = readWhole(Math.round(v), floorRule);
                        if (value !== null) editRich({ minBv: value });
                      }}
                    />
                  </div>
                )}
              </div>
            )}
            {burstsRun && (
              <label className="field-rich-part field-rich-also">
                <input
                  type="checkbox"
                  checked={rich.burstsOff}
                  disabled={!richEdit}
                  onChange={(e) => editRich({ burstsOff: e.target.checked })}
                />
                Turn the classic bursts off with this command
                <small className="field-radar-hint">
                  Rich readings take their place; the node keeps only the newest
                  command, so both go in one.
                </small>
              </label>
            )}
            {richCaution && (
              <div className="field-radar-caution" role="note">
                {`The battery reads ${richLow.battery.toFixed(2)} V, below the ${(rr.minBv ?? RICH_FLOOR_DEFAULT_MV).toLocaleString()} mV floor: the node skips ${skipped} until the cell recovers.`}
              </div>
            )}
            {errors.rich && (
              <div className="field-alert" role="alert">
                {errors.rich}
              </div>
            )}
            <div className="field-actions">
              <button
                type="button"
                className="field-button primary"
                disabled={!richSend || richNothing || richSame}
                onClick={() => send("rich", richValues)}
              >
                {busy === "rich"
                  ? "Sending…"
                  : nodeRich
                    ? "Update rich readings"
                    : "Start rich readings"}
              </button>
              <button
                type="button"
                className="field-button danger"
                disabled={!richSend || !nodeRich}
                onClick={() =>
                  send("rich", {
                    radar_rich: 0,
                    ...(upAllowed ? { radar_rich_up: 0 } : {}),
                  })
                }
              >
                Turn rich readings off
              </button>
            </div>
            {richSame && (
              <small className="field-radar-hint">
                That is what the node runs now.
              </small>
            )}
            {richNothing && (
              <small className="field-radar-hint">
                Tick at least one part
                {nodeRich ? ", or turn rich readings off" : ""}.
              </small>
            )}
            {richReq && trail(richReq, richStatus, null)}
            <Table
              label="Rich-reading settings, requested and reported"
              asked={asked}
              reported={reported}
              rows={[
                [
                  "radar_rich",
                  "On every wake",
                  (v) => (Number(v) === 0 ? "off" : richPartsText(v)),
                ],
                ...(upAllowed || rr.up !== null
                  ? [
                      [
                        "radar_rich_up",
                        "As well on upload wakes",
                        (v) => (Number(v) === 0 ? "nothing" : richPartsText(v)),
                      ],
                    ]
                  : []),
                ...(everyAllowed || rr.every !== null
                  ? [["radar_rich_every", "Taken on", (v) => wakeEveryText(v)]]
                  : []),
                ...(floorAllowed || rr.minBv !== null
                  ? [
                      [
                        "radar_rich_min_bv",
                        "Battery floor",
                        (v) => `${Number(v).toLocaleString()} mV`,
                      ],
                    ]
                  : []),
              ]}
            />
            {richRuns && (
              <p className="field-rich-note">
                The node runs: {richSettingsText(rr)}.
              </p>
            )}
            <More label="Counters">
              <dl
                className="field-facts field-rich-counters"
                aria-label="The node's rich-reading counters"
              >
                {arrived && (
                  <Fact label="Received, last 24 h">
                    {arrivedText(arrived)}
                  </Fact>
                )}
                {fromReadings && (
                  <Fact label="Captured and failed">
                    {readingCountsText(fromReadings, uncounted)}
                  </Fact>
                )}
                <Fact label="The node's counters">
                  {countersWords
                    ? `${countersWords}${shownCounters.source === "reading" ? ` (its reading of ${sgt(shownCounters.t)})` : ` · ${countersNote(shownCounters, arrived, { at: countersAt, build })}`}`
                    : "Not reported yet"}
                </Fact>
              </dl>
              <small className="field-radar-hint">
                Captured and failed: the captures the node took; waiting to
                upload: readings queued in its flash for the next upload;
                dropped: readings it had no room to queue.
              </small>
            </More>
          </Fold>
        )}
        {edgeShown && (
          <Fold
            id="field-rich-edge"
            title="On-node estimate"
            chip={edgeChip}
            tone={edgeTone}
          >
            <p className="field-rich-note">
              The node works out the level itself from each beat signal; Radar
              analysis compares it with our estimate.
            </p>
            <More label="Why?" className="is-why">
              <p className="field-rich-note">
                It sends its answer with the capture and the wake's reading.
                With the leading edge (firmware rtct.62) it also sends the
                strongest echoes in its gate, so the server can decide from them
                when a raw capture stays on the node.
              </p>
            </More>
            {!edgeAllowed && (
              <div className="field-notice">
                This server does not accept the on-node estimate settings
                (radar_edge) yet; it needs the updated backend.
              </div>
            )}
            {edgeAllowed && re.edge === null && (
              <p className="field-rich-note">
                The node has not reported radar_edge yet. Firmware rtct.61 or
                later reports it with its other settings; older firmware ignores
                it.
              </p>
            )}
            <div
              className="field-edge-choices"
              role="radiogroup"
              aria-label="What the node estimates"
            >
              {EDGE_CHOICES.filter(
                ([value]) => !EDGE_BENCH.includes(value),
              ).map(edgeChoice)}
              <More
                label="Bench-test options"
                open={
                  EDGE_BENCH.includes(edge.edge) || EDGE_BENCH.includes(re.edge)
                }
              >
                {EDGE_CHOICES.filter(([value]) =>
                  EDGE_BENCH.includes(value),
                ).map(edgeChoice)}
              </More>
            </div>
            {edge.edge > 0 && edgeFirmware && (
              <div className="field-radar-caution" role="note">
                {edgeFirmware}
              </div>
            )}
            {edge.edge > 0 && !capturesBeat && (
              <small className="field-radar-hint">
                The node estimates only the beat signals it captures, and at
                these settings it captures none: take the beat signal in Rich
                readings, above.
              </small>
            )}
            <div
              className="field-edge-part"
              role="group"
              aria-labelledby="field-rich-gate"
            >
              <h4 id="field-rich-gate" className="field-edge-subhead">
                Search gate
              </h4>
              <p className="field-rich-note">
                Where the node looks for the level, in mm from the radar (its
                own default is{" "}
                {gateText(EDGE_DEFAULTS.gateMin, EDGE_DEFAULTS.gateMax)}).
              </p>
              {gate ? (
                <div className="field-rich-recommended" role="note">
                  <div>
                    <p>
                      <b>Suggested: {gateText(gate.min, gate.max)}</b>, so the
                      node searches where the server does.
                    </p>
                    <More label="Why this gate?" className="is-why">
                      <p className="field-rich-note">
                        {gateBasisText(gate)} {gateMatchText(gate)}
                      </p>
                    </More>
                  </div>
                  <button
                    type="button"
                    className="field-button"
                    disabled={!edgeEdit || !gateAllowed || gateChosen}
                    onClick={() =>
                      editEdge({ gateMin: gate.min, gateMax: gate.max })
                    }
                  >
                    {!gateChosen
                      ? "Apply the suggested gate"
                      : gateRuns
                        ? "The node runs the suggested gate"
                        : gateAsked
                          ? "Suggested gate requested"
                          : "Suggested gate applied"}
                  </button>
                </div>
              ) : (
                suggestedGate === null && (
                  <small className="field-radar-hint">
                    The server suggests no gate yet: from the backend's .62
                    support on, the radar profile (Radar analysis) suggests the
                    gate that matches the server's own search.
                  </small>
                )
              )}
              {gateChosen && !gateRuns && !gateAsked && touched.edge && (
                <small className="field-radar-hint">
                  Applied to the gate below: {edgeLabel} sends it to the node
                  with the rest of this section.
                </small>
              )}
              <div className="field-rich-numbers">
                {[
                  ["radar_gate_min_mm", "gateMin", "Search from (mm)"],
                  ["radar_gate_max_mm", "gateMax", "Search to (mm)"],
                ].map(([key, field, label]) => {
                  const [lo, hi] = edgeRule(rules, key);
                  return (
                    <div className="field-burst-number" key={key}>
                      <label htmlFor={`field-rich-${field}`}>
                        {label}
                        <small>
                          {lo}–{hi.toLocaleString()}.
                        </small>
                      </label>
                      <NumberDraft
                        id={`field-rich-${field}`}
                        min={lo}
                        max={hi}
                        step={1}
                        value={edge[field]}
                        disabled={!edgeEdit || !gateAllowed}
                        onChange={(v) => {
                          const value = readWhole(Math.round(v), [lo, hi]);
                          if (value !== null) editEdge({ [field]: value });
                        }}
                      />
                    </div>
                  );
                })}
              </div>
              {gateBad && (
                <small className="field-radar-hint">
                  The gate must start nearer the radar than it ends.
                </small>
              )}
            </div>
            <div
              className="field-edge-part"
              role="group"
              aria-labelledby="field-rich-keep"
            >
              <h4 id="field-rich-keep" className="field-edge-subhead">
                Raw uploads
              </h4>
              <p className="field-rich-note">
                With the leading edge, the node can keep most raw captures and
                send only the reading: fewer uploads, less data and battery.
              </p>
              <More label="Why?" className="is-why">
                <p className="field-rich-note">
                  The reading carries its own estimate and echoes (firmware
                  rtct.62). The node always sends the raw captures when no echo
                  stands in its gate, the beat signal clips or it has no
                  estimate.
                </p>
              </More>
              {!keepAllowed && edgeAllowed && (
                <div className="field-notice">
                  This server does not accept the raw-upload settings
                  (radar_keep_every, radar_keep_mm, radar_keep_db) yet; it needs
                  the backend's .62 support.
                </div>
              )}
              {keepAllowed && !keepUsable && (
                <p className="field-rich-note is-off">
                  Only with the leading edge (v2): choose it above to hold raw
                  captures back. With any other estimate the node sends every
                  rich wake's raw captures.
                </p>
              )}
              {keepAllowed && keepUsable && !richBeat && (
                <small className="field-radar-hint">
                  Raw uploads apply to rich readings with the beat signal; at
                  these settings the node takes none (Rich readings, above).
                </small>
              )}
              <div className="field-rich-numbers">
                {KEEP_NUMBERS.map(([key, field, label, help]) => {
                  const [lo, hi] = edgeRule(rules, key);
                  return (
                    <div className="field-burst-number" key={key}>
                      <label htmlFor={`field-rich-keep-${field}`}>
                        {label}
                        <small>
                          {help} {lo}–{hi.toLocaleString()}.
                        </small>
                      </label>
                      <NumberDraft
                        id={`field-rich-keep-${field}`}
                        min={lo}
                        max={hi}
                        step={1}
                        value={edge[field]}
                        disabled={!edgeEdit || !keepAllowed || !keepUsable}
                        onChange={(v) => {
                          const value = readWhole(Math.round(v), [lo, hi]);
                          if (value !== null) editEdge({ [field]: value });
                        }}
                      />
                    </div>
                  );
                })}
              </div>
              {keepAllowed && keepUsable && edge.every > 1 && (
                <div className="field-radar-caution" role="note">
                  Over 1, the rich wakes in between keep their raw captures on
                  the node and send only their reading: the server analyses
                  those held-back wakes from the node's echo list, without the
                  obstruction, lens and two-reflector checks that need the beat
                  signal, and Radar analysis draws them hollow.
                </div>
              )}
              {(keepAllowed || nowFigures?.keep) && (
                <EdgeCosts
                  now={nowFigures}
                  planned={edgePlanned ? planFigures : null}
                  waiting={!projection || (edgePlanned && !projectionNow)}
                  failed={
                    projectionFailed || (edgePlanned && projectionNowFailed)
                  }
                  answered={!!projected}
                  onNet={onNet}
                />
              )}
              {lastKeep && (
                <dl
                  className="field-facts field-rich-counters"
                  aria-label="The newest rich wake's raw upload"
                >
                  <Fact label="Newest rich wake">
                    {`${lastKeep.t ? `${sgt(lastKeep.t)}: ` : ""}${keepNoteText(lastKeep.value, nodeEdge, { short: true }) || "no note"}`}
                  </Fact>
                </dl>
              )}
            </div>
            {errors.edge && (
              <div className="field-alert" role="alert">
                {errors.edge}
              </div>
            )}
            <div className="field-actions">
              <button
                type="button"
                className="field-button primary"
                disabled={!edgeSend || edgeSame || gateBad}
                onClick={() => send("edge", edgeValues)}
              >
                {edgeLabel}
              </button>
            </div>
            {edgeSame && (
              <small className="field-radar-hint">
                That is what the node runs now.
              </small>
            )}
            {edgeReq && trail(edgeReq, edgeStatus, null)}
            <Table
              label="On-node estimate settings, requested and reported"
              asked={asked}
              reported={reported}
              rows={[
                ["radar_edge", "Estimate", (v) => edgeName(Number(v))],
                ...(gateAllowed || re.gateMin !== null || re.gateMax !== null
                  ? [
                      [
                        "radar_gate_min_mm",
                        "Search from",
                        (v) => `${Number(v).toLocaleString()} mm`,
                      ],
                      [
                        "radar_gate_max_mm",
                        "Search to",
                        (v) => `${Number(v).toLocaleString()} mm`,
                      ],
                    ]
                  : []),
                ...(keepAllowed || re.every !== null
                  ? [
                      [
                        "radar_keep_every",
                        "Raw captures on",
                        (v) =>
                          Number(v) > 1
                            ? `every ${ordinal(Number(v))} rich wake`
                            : "every rich wake",
                      ],
                      [
                        "radar_keep_mm",
                        "Also on a move over",
                        (v) =>
                          Number(v) > 0
                            ? `${Number(v).toLocaleString()} mm`
                            : "off",
                      ],
                      [
                        "radar_keep_db",
                        "Also with a runner-up within",
                        (v) =>
                          Number(v) > 0
                            ? `${Number(v).toLocaleString()} dB`
                            : "off",
                      ],
                    ]
                  : []),
              ]}
            />
            {(re.edge ?? 0) > 0 && (
              <p className="field-rich-note">
                The node runs:{" "}
                {edgeSettingsText({
                  edge: re.edge,
                  gateMin: re.gateMin,
                  gateMax: re.gateMax,
                  every: KEEP_EDGES.includes(re.edge) ? re.every : null,
                  mm: KEEP_EDGES.includes(re.edge) ? re.mm : null,
                  db: KEEP_EDGES.includes(re.edge) ? re.db : null,
                })}
                .
              </p>
            )}
          </Fold>
        )}
        {rawShown && (
          <Fold
            id="field-rich-raw"
            title="Raw output (streaming and bursts)"
            chip={rawChip}
            tone="is-off"
            open={rawOpen}
          >
            <p className="field-rich-note">
              One setting for streaming and bursts: a capture holds the spectrum
              or the beat signal
              {altAllowed ? "; alternating brings both, in turn" : ""}. It goes
              with the next stream or burst command, or alone.
            </p>
            {modeAllowed && (
              <RawModeSelect
                id="field-rich-mode"
                value={modeShown}
                options={rawModeChoices(modeRule, modeShown, reportedMode)}
                disabled={busy !== null}
                onChange={(mode) => setRaw((old) => ({ ...old, mode }))}
                reported={reportedMode}
                requested={requestedMode}
              />
            )}
            {altAllowed && (
              <RawAltToggle
                id="field-rich-alt"
                checked={altShown === 1}
                disabled={busy !== null}
                onChange={(alt) => setRaw((old) => ({ ...old, alt }))}
                reported={reportedAlt}
                requested={requestedAlt}
              />
            )}
            {errors.raw && (
              <div className="field-alert" role="alert">
                {errors.raw}
              </div>
            )}
            {(modeAllowed || altAllowed) && (
              <div className="field-actions">
                <button
                  type="button"
                  className="field-button"
                  disabled={busy !== null || !!inFlight || !rawChanged}
                  onClick={() => send("raw", rawChanges)}
                >
                  {rawLabel}
                </button>
              </div>
            )}
            {rawReq && trail(rawReq, shownStatus(rawReq, settings), null)}
            <Table
              label="Raw output, requested and reported"
              asked={asked}
              reported={reported}
              rows={[
                [
                  "radar_raw_mode",
                  "Raw output mode",
                  (v) => rawModeText(Number(v)),
                ],
                ...(altAllowed || reportedAlt !== null
                  ? [
                      [
                        "radar_raw_alt",
                        "Alternate spectrum and beat signal",
                        onOff,
                      ],
                    ]
                  : []),
              ]}
            />
          </Fold>
        )}
        {linkShown && (
          <Fold
            id="field-rich-link"
            title="Radar link"
            chip={linkChip}
            tone={linkTone}
            open={linkOpen}
          >
            <p className="field-rich-note">
              The radar's serial speed: faster means shorter captures and less
              time awake. 9,600 baud is its factory rate and the way back.
            </p>
            <More label="Why?" className="is-why">
              <p className="field-rich-note">
                One of its packets (about 1&nbsp;kB) takes about {onWire(9600)}{" "}
                at 9,600 baud, {onWire(38400)} at 38,400 and {onWire(115200)} at
                115,200, and streaming every 5 s needs 38,400 or more. The
                switch writes the radar's flash, so the node makes it only on a
                confirmed firmware and above the battery floor.
              </p>
            </More>
            <dl
              className="field-facts field-rich-link"
              aria-label="The radar link"
            >
              <Fact label="Rate">
                {reportedBaud !== null
                  ? `${baudText(reportedBaud)}: the rate the radar answers at`
                  : found?.bd !== null && found?.bd !== undefined
                    ? `${baudText(found.bd)}: the rate the node last found the radar answering at`
                    : `Not reported yet: the radar starts at its factory ${BAUD_FACTORY.toLocaleString()} baud`}
              </Fact>
              {found && (
                <Fact label="Last detection">
                  {`${detection.t ? `${sgt(detection.t)}: ` : ""}${baudFoundText(found)}`}
                </Fact>
              )}
              {lastSwitch && (
                <Fact label="Last switch">
                  {`${switchRecord.t ? `${sgt(switchRecord.t)}: ` : ""}${baudSwitchText(lastSwitch)}`}
                </Fact>
              )}
            </dl>
            {baudAllowed && (
              <div className="field-stream-mode">
                <label htmlFor="field-rich-baud">Switch the radar to</label>
                <select
                  id="field-rich-baud"
                  value={baudShown}
                  disabled={busy !== null}
                  onChange={(e) => setBaud(Number(e.target.value))}
                >
                  {baudChoices.map((b) => (
                    <option key={b} value={b}>
                      {baudText(b)}
                    </option>
                  ))}
                </select>
                {requestedBaud !== null && requestedBaud !== reportedBaud && (
                  <small className="field-radar-hint">
                    {requestedBaud.toLocaleString()} baud requested
                  </small>
                )}
              </div>
            )}
            {errors.baud && (
              <div className="field-alert" role="alert">
                {errors.baud}
              </div>
            )}
            {baudAllowed && (
              <div className="field-actions">
                <button
                  type="button"
                  className="field-button"
                  disabled={busy !== null || !!inFlight || baudChange === null}
                  onClick={() => send("baud", { radar_baud: baudChange })}
                >
                  {busy === "baud" ? "Sending…" : "Change the radar link"}
                </button>
              </div>
            )}
            {baudReq &&
              trail(
                baudReq,
                baudStatus,
                null,
                baudStatus === "acknowledged"
                  ? "received by the node: it switches the radar on its next wake"
                  : null,
              )}
            <Table
              label="Radar link, requested and reported"
              asked={asked}
              reported={reported}
              rows={[
                [
                  "radar_baud",
                  "Rate",
                  (v) => `${Number(v).toLocaleString()} baud`,
                ],
              ]}
            />
          </Fold>
        )}
        <p className="field-caption" style={{ margin: 0 }}>
          What these cost, in battery and data, is under Before you apply. A
          command reaches the node with its next upload and runs on the wake
          after; on cellular that can take about an hour.
        </p>
      </div>
    </section>
  );
}
